// src/components/personal/sections/ClientsSection/hooks/useClientStatus.ts
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/services/api';
import { useBusinessId } from '@/hooks/useBusinessId';
import type { 
  StatusSettingsType,
  ClientStats,
  APIError
} from '../types/types';
import { displayErrorNotification } from '../error/error-handlers';
import { DEFAULT_STATS, DEFAULT_AUTO_CALCULATION_REQUIREMENTS } from '../utils/constants';

interface UseClientStatusState {
  statusSettings: StatusSettingsType | null;
  loading: boolean;
  error: APIError | null;
  modalError: APIError | null; 
  clientStats: ClientStats; // Оставляем для совместимости API
  autoCalculationRequirements: {
    MIN_REGULAR_CLIENTS: number;
    MIN_LOYAL_CLIENTS: number;
  };
  lastSuccessfulSettings: StatusSettingsType | null;
}

interface UseClientStatusResult extends UseClientStatusState {
  showStatusSettings: boolean;
  toggleStatusSettings: () => void;
  updateStatusSettings: (settings: StatusSettingsType) => Promise<boolean>;
  recalculateClientStatuses: () => Promise<any>;
  handleStatusSettingsSave: () => Promise<boolean>;
}

const initialState: UseClientStatusState = {
  statusSettings: null,
  loading: false,
  error: null,
  modalError: null,
  clientStats: DEFAULT_STATS, // Оставляем для совместимости API
  autoCalculationRequirements: DEFAULT_AUTO_CALCULATION_REQUIREMENTS,
  lastSuccessfulSettings: null
};

/**
 * Хук для управления статусами клиентов
 */
export const useClientStatus = (): UseClientStatusResult => { // Явно указываем тип возвращаемого значения
  const { t } = useTranslation();
  const { businessId, isLoading: isBusinessIdLoading } = useBusinessId();
  
  const [state, setState] = useState<UseClientStatusState>(initialState);
  
  const [showStatusSettings, setShowStatusSettings] = useState(false);
  const [updateInProgress, setUpdateInProgress] = useState<boolean>(false);
  const [recalculateInProgress, setRecalculateInProgress] = useState<boolean>(false);

  /**
   * Глубокое сравнение настроек для определения изменений
   */
  const compareSettings = (
    currentSettings: StatusSettingsType | null, 
    newSettings: StatusSettingsType
  ): { hasChanges: boolean, changes: Record<string, { current: any, new: any }> } => {
    if (!currentSettings) return { hasChanges: true, changes: {} };

    const keys: (keyof StatusSettingsType)[] = [
      'ordersX', 
      'ordersZ', 
      'intervalA', 
      'intervalB', 
      'daysC'
    ];

    const changes: Record<string, { current: any, new: any }> = {};

    const hasChanges = keys.some(key => {
      // Преобразуем к числу для корректного сравнения
      const currentValue = Number(currentSettings[key]);
      const newValue = Number(newSettings[key]);
      
      // Строгое сравнение с приведением к числу
      const isDifferent = currentValue !== newValue;
      
      if (isDifferent) {
        changes[key as string] = { 
          current: currentValue, 
          new: newValue 
        };
      }

      return isDifferent;
    });

    return { hasChanges, changes };
  };

  /**
   * Загрузка настроек статусов клиентов
   */
  const fetchStatusSettings = useCallback(async () => {
    // Валидация businessId
    if (isBusinessIdLoading) return null;
    if (!businessId) {
      setState(prev => ({
        ...prev,
        modalError: {
          message: t('errors.business_id_unavailable'),
          code: 'BUSINESS_ID_UNAVAILABLE'
        }
      }));
      return null;
    }
    
    setState(prev => ({
      ...prev,
      loading: true,
      modalError: null
    }));
    
    try {
      // Запрос настроек с сервера
      const response = await api.get(`/api/clients/status-settings/?business=${businessId}`);
      
      if (response && response.success) {
        // Создаем объект настроек с корректной структурой
        const settings: StatusSettingsType = {
          id: response.id,
          ordersX: Number(response.ordersX),
          ordersZ: Number(response.ordersZ),
          intervalA: Number(response.intervalA),
          intervalB: Number(response.intervalB),
          daysC: Number(response.daysC),
          created_at: response.created_at,
          updated_at: response.updated_at,
          average_intervals: response.average_intervals
        };
        
        setState(prev => ({
          ...prev,
          loading: false,
          modalError: null,
          statusSettings: {...settings},
          lastSuccessfulSettings: {...settings}
          // Удаляем обновление clientStats
        }));
        
        return settings;
      }
      
      throw new Error(t('errors.load_settings_failed'));
    } catch (error: any) {
      console.error(t('errors.status_settings_load_error'), error);
      
      setState(prev => ({
        ...prev,
        loading: false,
        modalError: {
          message: t('errors.data_load_error'),
          code: error.code || 'UNKNOWN_ERROR'
        },
        statusSettings: prev.lastSuccessfulSettings
      }));
      
      return null;
    }
  }, [businessId, isBusinessIdLoading, t]);

  /**
   * Обновление настроек статусов
   */
  const updateStatusSettings = useCallback(async (newSettings: StatusSettingsType) => {
    // Валидация
    if (isBusinessIdLoading || !businessId) {
      setState(prev => ({
        ...prev,
        modalError: {
          message: t('errors.business_id_unavailable'),
          code: 'BUSINESS_ID_UNAVAILABLE'
        }
      }));
      return false;
    }
    
    // Предотвращение дублирования запросов
    if (updateInProgress) {
      console.warn(t('logs.duplicate_request_prevented'));
      return false;
    }

    // Проверяем, есть ли реальные изменения
    const { hasChanges } = compareSettings(state.statusSettings, newSettings);
    
    // Если нет изменений, немедленно возвращаем true
    if (!hasChanges) {
      console.log('No actual changes in settings, skipping update');
      return true;
    }
    
    setUpdateInProgress(true);
    setState(prev => ({ ...prev, loading: true }));
    
    try {
      // Подготовка данных для отправки
      const dataToSend = {
        ordersX: Number(newSettings.ordersX),
        ordersZ: Number(newSettings.ordersZ),
        intervalA: Number(newSettings.intervalA),
        intervalB: Number(newSettings.intervalB),
        daysC: Number(newSettings.daysC),
        business: businessId
      };
      
      // Отправка на сервер
      const response = await api.put(`/api/clients/status-settings/update/`, dataToSend);
      
      // Проверяем успешность ответа
      if (response && response.success) {
        // Создаем обновленный объект настроек с учетом структуры ответа
        const updatedSettings: StatusSettingsType = {
          id: response.id,
          ordersX: Number(response.ordersX),
          ordersZ: Number(response.ordersZ),
          intervalA: Number(response.intervalA),
          intervalB: Number(response.intervalB),
          daysC: Number(response.daysC),
          created_at: response.created_at,
          updated_at: response.updated_at,
          average_intervals: response.average_intervals
        };
        
        // Обновление состояния (удаляем обновление статистики)
        setState(prev => {
          return {
            ...prev,
            loading: false,
            modalError: null,
            statusSettings: updatedSettings,
            lastSuccessfulSettings: {...updatedSettings}
            // Удаляем обновление clientStats
          };
        });
        
        return true;
      }
      
      throw new Error(response.error || t('errors.update_settings_failed'));
    } catch (error: any) {
      console.error(t('errors.update_settings_error'), error);
      
      setState(prev => ({
        ...prev,
        loading: false,
        modalError: {
          message: t('errors.settings_save_error'),
          code: error.response?.data?.error || error.code || 'UNKNOWN_ERROR'
        },
        statusSettings: state.lastSuccessfulSettings
      }));
      
      displayErrorNotification({
        message: t('errors.settings_save_error'),
        code: error.response?.data?.error || error.code || 'UNKNOWN_ERROR'
      });
      
      return false;
    } finally {
      setUpdateInProgress(false);
    }
  }, [businessId, isBusinessIdLoading, t, state.statusSettings, state.lastSuccessfulSettings, updateInProgress]);
 
  /**
   * Сохранение настроек статусов из модального окна
   */
  const handleStatusSettingsSave = useCallback(async () => {
    // Проверка, что настройки доступны
    if (!state.statusSettings) {
      return false;
    }
    
    // Выполняем обновление настроек и дожидаемся результата
    const success = await updateStatusSettings(state.statusSettings);
    
    // Если настройки успешно сохранены, закрываем модальное окно
    if (success) {
      setShowStatusSettings(false);
      return true;
    } else {
      displayErrorNotification({
        message: t('errors.settings_save_error'),
        code: 'UPDATE_SETTINGS_FAILED'
      });
      return false;
    }
  }, [state.statusSettings, updateStatusSettings, t]);
 
  /**
   * Пересчет статусов клиентов
   * Сохраняем для функциональности системы
   */
  const recalculateClientStatuses = useCallback(async () => {
    // Валидация
    if (isBusinessIdLoading || !businessId) {
      setState(prev => ({
        ...prev,
        error: {
          message: t('errors.business_id_unavailable'),
          code: 'BUSINESS_ID_UNAVAILABLE'
        }
      }));
      return null;
    }
    
    // Предотвращение дублирования
    if (recalculateInProgress) {
      console.warn(t('logs.duplicate_recalc_prevented'));
      return null;
    }
    
    // Устанавливаем флаг выполнения
    setRecalculateInProgress(true);
    
    setState(prev => ({
      ...prev,
      loading: true,
      error: null
    }));
    
    try {
      // Запрос пересчета статусов
      const response = await api.post(`/api/clients/recalculate-statuses/`, { business: businessId });
      
      if (response && response.success) {
        setState(prev => ({
          ...prev,
          loading: false,
          error: null
          // Удаляем обновление clientStats
        }));
        
        return true; // Возвращаем true вместо updatedClients
      }
      
      if (response && response.error) {
        throw new Error(response.error);
      }
      
      throw new Error(t('errors.unexpected_api_format'));
    } catch (error: any) {
      console.error(t('errors.recalculate_error'), error);
      
      setState(prev => ({
        ...prev,
        loading: false,
        error: {
          message: t('errors.recalculate_statuses_error'),
          code: error.code || 'UNKNOWN_ERROR'
        }
      }));
      
      displayErrorNotification({
        message: t('errors.recalculate_statuses_error'),
        code: error.code || 'UNKNOWN_ERROR'
      });
      
      return null;
    } finally {
      // Сбрасываем флаг выполнения
      setRecalculateInProgress(false);
    }
  }, [businessId, isBusinessIdLoading, t, recalculateInProgress]);

  /**
   * Открытие/закрытие модального окна настроек
   */
  const toggleStatusSettings = useCallback(() => {
    // Проверка, что нет активных запросов
    if (updateInProgress) {
      console.warn(t('logs.modal_toggle_prevented'));
      return;
    }
    
    const newShowStatus = !showStatusSettings;
    setShowStatusSettings(newShowStatus);
    
    if (newShowStatus) {
      // При открытии окна загружаем свежие настройки
      fetchStatusSettings();
    } else {
      // При закрытии очищаем ошибки и сбрасываем статусы загрузки
      setState(prev => ({
        ...prev,
        modalError: null,
        loading: false // Сбрасываем состояние загрузки при закрытии
      }));
      
      // Важно: гарантируем, что recalculateInProgress сброшен
      setRecalculateInProgress(false);
    }
  }, [showStatusSettings, fetchStatusSettings, updateInProgress, t]);

  return {
    ...state,
    showStatusSettings,
    toggleStatusSettings,
    updateStatusSettings,
    recalculateClientStatuses,
    handleStatusSettingsSave
  };
};

export default useClientStatus;